import { useState, useMemo, useCallback, useEffect } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortState {
  column: string | null;
  direction: SortDirection;
}

export function useSortableTable<T>(
  items: T[],
  defaultSort?: { column: string; direction: 'asc' | 'desc' },
  options?: {
    getSortValue?: (item: T, column: string) => unknown;
    compare?: (a: T, b: T, column: string, dir: 1 | -1) => number | null;
    /** When this changes, sort resets to `defaultSort` (e.g. active ↔ archive tab). */
    resetKey?: string;
  },
) {
  const [sort, setSort] = useState<SortState>({
    column: defaultSort?.column ?? null,
    direction: defaultSort?.direction ?? null,
  });

  useEffect(() => {
    if (options?.resetKey === undefined) return;
    setSort({
      column: defaultSort?.column ?? null,
      direction: defaultSort?.direction ?? null,
    });
  }, [options?.resetKey, defaultSort?.column, defaultSort?.direction]);

  const toggleSort = useCallback((column: string) => {
    setSort(prev => {
      if (prev.column !== column) return { column, direction: 'asc' };
      if (prev.direction === 'asc') return { column, direction: 'desc' };
      return { column: null, direction: null };
    });
  }, []);

  const sorted = useMemo(() => {
    const activeSort = sort.column && sort.direction
      ? sort
      : defaultSort
        ? { column: defaultSort.column, direction: defaultSort.direction as SortDirection }
        : null;

    if (!activeSort?.column || !activeSort.direction) return items;
    const col = activeSort.column;
    const dir = activeSort.direction === 'asc' ? 1 : -1;

    return [...items].sort((a, b) => {
      const custom = options?.compare?.(a, b, col, dir);
      if (custom != null) return custom;

      const aVal = options?.getSortValue?.(a, col) ?? (a as Record<string, unknown>)[col];
      const bVal = options?.getSortValue?.(b, col) ?? (b as Record<string, unknown>)[col];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * dir;
      }

      // Try date parsing
      const aDate = Date.parse(String(aVal));
      const bDate = Date.parse(String(bVal));
      if (!isNaN(aDate) && !isNaN(bDate)) {
        return (aDate - bDate) * dir;
      }

      return String(aVal).localeCompare(String(bVal)) * dir;
    });
  }, [items, sort, defaultSort, options?.getSortValue, options?.compare]);

  return { sorted, sort, toggleSort };
}

interface SortableThProps {
  column: string;
  sort: SortState;
  onSort: (column: string) => void;
  children: React.ReactNode;
  className?: string;
}

const sortableThBase =
  'text-left font-medium text-muted-foreground py-3 px-2 max-md:px-1.5 max-md:py-2 max-md:text-[11px] cursor-pointer select-none hover:text-foreground transition-colors';

export const SortableTh = ({ column, sort, onSort, children, className = '' }: SortableThProps) => {
  const isActive = sort.column === column;

  return (
    <th
      className={`${sortableThBase} ${className}`}
      onClick={() => onSort(column)}
    >
      <span className="inline-flex items-center gap-0.5 max-md:gap-0">
        <span className="truncate">{children}</span>
        {isActive && sort.direction === 'asc' && <ArrowUp className="h-3 w-3 max-md:h-2.5 max-md:w-2.5 shrink-0" />}
        {isActive && sort.direction === 'desc' && <ArrowDown className="h-3 w-3 max-md:h-2.5 max-md:w-2.5 shrink-0" />}
        {!isActive && <ArrowUpDown className="h-3 w-3 max-md:h-2.5 max-md:w-2.5 opacity-30 shrink-0 max-md:hidden" />}
      </span>
    </th>
  );
};

interface StatusPinThProps {
  onCycle: () => void;
  children: React.ReactNode;
  className?: string;
}

/** Status column — cycles which state is floated to the top; same look as other headers. */
export const StatusPinTh = ({ onCycle, children, className = '' }: StatusPinThProps) => (
  <th
    className={`${sortableThBase} ${className}`}
    onClick={onCycle}
  >
    <span className="inline-flex items-center gap-0.5 max-md:gap-0">
      <span className="truncate">{children}</span>
      <ArrowUpDown className="h-3 w-3 max-md:h-2.5 max-md:w-2.5 opacity-30 shrink-0 max-md:hidden" />
    </span>
  </th>
);
